import { TransformedRevenueData } from 'services/api/type/report.type';
import { Table } from 'antd';
import { getColumnConsolidateSourceRevenue, getColumnsConsolidateRevenue } from 'constants/table';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useGetConsolidateSourceRevenueDetails } from 'hooks/uesGetConsolidateRevenue';
import { PAGE_SIZE_CONSOLIDATE_SOURCE_DETAILS } from 'services/api/module/report.api';
import { useTranslation } from 'react-i18next';

interface CustomTableProps {
  loading?: boolean;
  dataSource: TransformedRevenueData[];
  onShowOcc?: boolean;
  title: string;
  filterOptions?: any;
  branch_ids?: string;
  setDataSource?: (data: any[]) => void;
}

interface ExpandedRowState {
  sourceId: number;
  page: number;
  pageSize: number;
}

export default function CustomerTableForConsolidationRevenue({
  loading,
  dataSource,
  onShowOcc,
  title,
  filterOptions,
  branch_ids,
  setDataSource
}: CustomTableProps) {
  // Define the custom sort order for sources
  const sourceOrder = useMemo(
    () => [
      'SO - OTA',
      'SO - TA',
      'SO - Corp',
      'SO - FIT',
      'SO - Walk-in',
      'SO-Direct Booking',
      'Others'
    ],
    []
  );
  const { t } = useTranslation();
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [expandedRows, setExpandedRows] = useState<Map<number, ExpandedRowState>>(new Map());
  const [isSorted, setIsSorted] = useState<boolean>(false);

  // Function to sort data by source
  const sortDataBySource = useCallback(
    (data: TransformedRevenueData[]): TransformedRevenueData[] => {
      return [...data].sort((a, b) => {
        const aIndex = sourceOrder.findIndex(source => a.roomType?.includes(source));
        const bIndex = sourceOrder.findIndex(source => b.roomType?.includes(source));

        // If both found in order list, sort by order
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        // If only a is found, a comes first
        if (aIndex !== -1 && bIndex === -1) {
          return -1;
        }

        // If only b is found, b comes first
        if (aIndex === -1 && bIndex !== -1) {
          return 1;
        }

        // If neither found, maintain original order
        return 0;
      });
    },
    [sourceOrder]
  );

  // Get processed data source based on sorting state
  const processedDataSource = useMemo(() => {
    if (!dataSource) return [];

    if (isSorted && title === t('bookingPage.bookingList.source')) {
      return sortDataBySource(dataSource);
    }

    return dataSource;
  }, [dataSource, isSorted, title, t, sortDataBySource]);

  useEffect(() => {
    if (setDataSource && processedDataSource && title === t('bookingPage.bookingList.source')) {
      setDataSource(processedDataSource);
    }
  }, [processedDataSource, setDataSource, title, t]);

  const handleSortToggle = useCallback(() => {
    setIsSorted(!isSorted);
  }, [isSorted]);

  const columnsConsolidateRevenue = useMemo(
    () =>
      getColumnsConsolidateRevenue(title, t, onShowOcc, {
        isSourceTab: title === t('bookingPage.bookingList.source'),
        isSorted,
        onSortToggle: handleSortToggle
      }),
    [title, t, onShowOcc, isSorted, handleSortToggle]
  );

  const handleExpand = (expanded: boolean, record: TransformedRevenueData) => {
    const sourceId = record.source_id;

    if (!sourceId) return;

    setExpandedRowKeys(prevKeys => {
      if (expanded) {
        return [...prevKeys, sourceId];
      } else {
        return prevKeys.filter(key => key !== sourceId);
      }
    });

    setExpandedRows(prevRows => {
      const newRows = new Map(prevRows);
      if (expanded) {
        newRows.set(sourceId, {
          sourceId,
          page: 1,
          pageSize: PAGE_SIZE_CONSOLIDATE_SOURCE_DETAILS
        });
      } else {
        newRows.delete(sourceId);
      }
      return newRows;
    });
  };

  const handleChangePage = (page: number, sourceId: number) => {
    setExpandedRows(prevRows => {
      const newRows = new Map(prevRows);
      const currentRow = newRows.get(sourceId);
      if (currentRow) {
        newRows.set(sourceId, { ...currentRow, page });
      }
      return newRows;
    });
  };

  const handleChangePageSize = (sourceId: number, size: number) => {
    setExpandedRows(prevRows => {
      const newRows = new Map(prevRows);
      const currentRow = newRows.get(sourceId);
      if (currentRow) {
        newRows.set(sourceId, { ...currentRow, pageSize: size, page: 1 });
      }
      return newRows;
    });
  };

  const hasExpandableRows = useMemo(() => {
    return dataSource?.some(record => record.source_id !== undefined && record.source_id !== null);
  }, [dataSource]);

  const columnsConsolidateSourceRevenue = useMemo(() => {
    return getColumnConsolidateSourceRevenue(t);
  }, [t]);

  const ExpandedRowContent = ({ sourceId }: { sourceId: number }) => {
    const rowState = expandedRows.get(sourceId);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
      setIsExpanded(expandedRowKeys.includes(sourceId));
    }, [sourceId]);

    const { data, pagination, isLoading } = useGetConsolidateSourceRevenueDetails(
      {
        ...filterOptions,
        source_id: sourceId,
        branch_ids: branch_ids
      },
      rowState?.page || 1,
      rowState?.pageSize || PAGE_SIZE_CONSOLIDATE_SOURCE_DETAILS,
      isExpanded
    );

    return (
      <Table
        rowKey="source_id"
        size="small"
        bordered
        loading={isLoading}
        columns={columnsConsolidateSourceRevenue}
        dataSource={data}
        showHeader={false}
        className="custom-expend"
        locale={{
          emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
        }}
        pagination={{
          current: rowState?.page || 1,
          position: ['bottomCenter'],
          total: pagination.total,
          pageSize: rowState?.pageSize || PAGE_SIZE_CONSOLIDATE_SOURCE_DETAILS,
          locale: { items_per_page: `/ ${t('common.page')}` },
          showLessItems: true,
          showTotal: total => `${total} ${t('bookingPage.bookingList.item')}`,
          onChange: (page: number) => handleChangePage(page, sourceId),
          onShowSizeChange: (current, size) => handleChangePageSize(sourceId, size)
        }}
      />
    );
  };

  return (
    <Table
      loading={loading}
      rowKey="source_id"
      size="small"
      bordered
      columns={columnsConsolidateRevenue}
      dataSource={processedDataSource ?? []}
      rowClassName={record => (record.roomType === t('common.total') ? 'bold-row' : '')}
      pagination={false}
      locale={{
        emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
      }}
      scroll={{ x: 1200, y: 'calc(100vh - 400px)' }}
      expandable={
        hasExpandableRows
          ? {
              expandedRowKeys,
              onExpand: handleExpand,
              expandedRowRender: record => <ExpandedRowContent sourceId={record.source_id} />,
              rowExpandable: record => record.source_id !== undefined && record.source_id !== null,
              columnWidth: '32px'
            }
          : undefined
      }
    />
  );
}
